import {
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { EventService, EventState } from '@app/event/event.service';
import {
  filter,
  first,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  tap
} from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';
import { Event } from '@app/graphql/generated';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { ModalService } from '@app/shared/modal/modal.service';
import { config } from '@app/app.config';
import { combineLatest, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { SpeechService } from '@app/event/speech-engines/speech.service';
import { Page, RoutingService } from '@app/routing.service';
import { UserService } from '@app/user/user.service';
import { ResponsiveService } from '@app/utility/responsive.service';
import { InputService } from '@app/services/input.service';
import {
  SetDefaultDesktopInput,
  SetDefaultInput
} from '@app/shared/settings/settings.actions';
import { Store } from '@ngxs/store';
import { FormControl, FormGroup } from '@angular/forms';
import { ElectronService } from 'ngx-electron';
import { EditEventComponent } from '@app/shared/modal/edit-event/edit-event.component';
import gql from 'graphql-tag';
import { Apollo } from 'apollo-angular';
import { Socket } from 'ngx-socket-io';
import { ExportSubtitlesComponent } from '@app/shared/modal/export-subtitles/export-subtitles.component';
import { EnableBypassComponent } from '@app/shared/modal/enable-bypass/enable-bypass.component';

enum formKeys {
  selectedMicId = 'selectedMicId',
  selectedSystemAudioDeviceId = 'selectedSystemAudioDeviceId'
}

@Component({
  selector: 'app-event-wrapper',
  templateUrl: './event-wrapper.component.html',
  styleUrls: ['./event-wrapper.component.scss']
})
export class EventWrapperComponent implements OnInit, OnDestroy, OnChanges {

  constructor(
    public electronService: ElectronService,
    public eventService: EventService,
    private userService: UserService,
    private appModalService: ModalService,
    private routingService: RoutingService,
    private route: ActivatedRoute,
    private speech: SpeechService,
    public responsiveService: ResponsiveService,
    private inputService: InputService,
    private store: Store,
    private apollo: Apollo,
    private socket: Socket) {
  }

  watchQueryParamLangChange() {
    this.route.queryParams.pipe(
      untilDestroyed(this)
    ).subscribe((params) => {
      console.log(params);
      if (params.lang) {
        this.eventService.setLanguage(this.eventId, params.lang, { window: 1 });
      }
    });
  }

  get eventId() {
    return this.route.snapshot.params.eventId;
  }

  get hasStenoPermission() {
    return this.currentUser?.permissions?.find(perm => perm.name === 'steno');
  }

  get stenoBoxHeight() {
    return this.hasStenoPermission ? 72 : 0;
  }

  //event: Event;
  siteURL = config.httpURL;
  currentUser$: Observable<any>;
  showReturnToStreamButton: Observable<boolean>;
  uncompletedEvent: Observable<any>;
  isCurrentlyStreaming$: Observable<boolean>;
  isPaused$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  isCompleted$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  appEventHeight$: Observable<string>;
  currentPage: ReplaySubject<Page>;
  formKeys = formKeys;
  formGroup = new FormGroup({
    [formKeys.selectedMicId]: new FormControl(),
    [formKeys.selectedSystemAudioDeviceId]: new FormControl()
  });
  pollInterval;
  eventStatus: EventState;
  canEdit: boolean;

  currentUser: any;

  selectedInput: any;

  public inputOptions = this.inputService.inputDevicesAsSelectOptions().pipe(
    tap(options => {
      const { selectOptions, selectedOption } = options;

      // this.viewModel.form.patchValue({ input: defaultOption });
      this.selectedInput = selectedOption;
      this.formGroup.patchValue({
        [formKeys.selectedMicId]: selectedOption?.value
      });

    }),
    map(options => options.selectOptions)
  );

  public desktopInputOptions;
  public online = false;

  showSteno = false;
  stenoControl = new FormControl('');

  ngOnInit() {
    this.watchForAudioInputChanges();
    this.watchForDesktopInputChanges();
    //this.watchEventActive();
    this.setupAppEventHeight();
    
    this.isCurrentlyStreaming$ = this.speech.isRecording$;

    this.currentPage = new ReplaySubject(1);
    const page: Page = {
      name: this.routingService.activatedRoute.snapshot.data.name,
      e: this.routingService.activatedRoute
    };
    this.currentPage.next(page);
    this.currentUser$ = this.userService.currentUser();
    // this.isCurrentlyStreaming = this.speech.isRecording$;

    this.userService
      .currentUser()
      .pipe(
        untilDestroyed(this),
        tap((currentUser: any) => (this.currentUser = currentUser)),
        tap(console.log)
      )
      .subscribe();
    // this.isCurrentlyStreaming = this.speech.isRecording$;

    this.showReturnToStreamButton = combineLatest([
      this.currentPage,
      this.currentUser$
    ]).pipe(
      map(
        ([page, user]) =>
          !!user.uncompletedEvent &&
          page.e.snapshot.params.eventId !== user.uncompletedEvent.shortId
      )
    );

    this.uncompletedEvent = this.currentUser$.pipe(
      map(user => user.uncompletedEvent)
    );

    this.eventService.reset();
    this.startPolling();
    
    console.log('Initialized isPaused$');
    this.isPaused$.next(true);
    this.watchQueryParamLangChange();
  }

  startPolling() {
    this.updateEventStatus();
    this.pollInterval = setInterval(() => {
      this.updateEventStatus();
    }, 2_000);
  }

  updateEventStatus() {
    this.eventService.getEventStatus(this.eventId)
    .pipe(first())
    .subscribe((status: EventState) => {
      this.eventStatus = status;
      this.online = this.eventStatus.online;
      this.isCompleted$.next(this.eventStatus.completed);
      const now = Date.now();
      console.log(`Received event status: s:${status.ts} c:${now} diff:${now-status.ts}`);
      if (this.eventStatus.completed)
        this.stopEvent();
    });
  }

  stopPolling() {
    clearInterval(this.pollInterval);
  }

  setupAppEventHeight() {
    this.appEventHeight$ = this.eventService.isSplitScreen$.pipe(
      tap(() => {

      }),
      mergeMap(isSplitScreen =>
        this.responsiveService.resize.pipe(
          map(size => ({ size, isSplitScreen }))
        )
      ),
      map(({ isSplitScreen, size }) => {

        const windowHeight = size.window.height;
        const streamWrapperHeight = size.streamWrapper.height;

        const headerHeight = size.header.height;
        const padding = 55;
        const totalUpperHeight = streamWrapperHeight + headerHeight + padding;
        const streamHeight = isSplitScreen
          ? `calc(100vh - ${totalUpperHeight}px)`
          : `calc(100vh - ${totalUpperHeight}px)`;

        return streamHeight;
      }),
      shareReplay(1)
    );
    this.appEventHeight$.subscribe();
  }

  startRecording(service: string, lang?: string) {
    console.log('startRecording');
    this.speech.startRecording(service, this.eventStatus?.language);
    this.isPaused$.next(false);
    this.eventService.enableEncoders(this.eventId).subscribe();
    //if (this.eventService.resubscribeHandler)
    //  this.eventService.resubscribeHandler();
  }

  stopRecording(finalize: boolean = false) {
    console.log('stopRecording');
    this.isPaused$.next(true);
    this.speech.stopRecording(finalize);
    this.eventService.disableEncoders(this.eventId).subscribe();
    //if (this.eventService.unsubscribeHandler)
    //  this.eventService.unsubscribeHandler();
  }

  completeEvent() {
    this.eventService.disableEncoders(this.eventId, true).subscribe();
    this.isCurrentlyStreaming$.pipe(take(1)).subscribe(streaming =>
      {
        if (streaming)
          this.stopRecording(true);

        this.eventService
        .completeEvent()
        .pipe(
          take(1),
          map(() => {
            //this.event.completed = true;
            this.isCompleted$.next(true);
            return this.userService.refetchCurrentUser();
          })
        )
        .subscribe();
      });
  }

  editEvent() {
    this.appModalService.show<EditEventComponent>(EditEventComponent);
  }

  stopEvent() {
    this.stopPolling();
    this.stopRecording();
  }

  ngOnDestroy(): void {
    this.stopEvent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.responsiveService.detectChanges();
  }

  watchForAudioInputChanges() {
    this.formGroup
      .get(formKeys.selectedMicId)
      .valueChanges.pipe(
      map(option => {
        const micId = option?.id;
        this.store.dispatch(new SetDefaultInput(option));
        this.speech.isRecording$.pipe(take(1)).subscribe(isRecording => {
          if (isRecording) {
            this.stopRecording();
            this.startRecording(this.eventStatus.service);
          }
        });
      }),
      untilDestroyed(this)
    )
      .subscribe();
  }

  watchForDesktopInputChanges() {
    this.formGroup
      .get(formKeys.selectedSystemAudioDeviceId)
      .valueChanges.pipe(
      map(option => {
        const micId = option.value.id;
        this.store.dispatch(new SetDefaultDesktopInput(micId));
        this.speech.isRecording$.pipe(take(1)).subscribe(isRecording => {
          if (isRecording) {
            this.stopRecording();
            this.startRecording(this.eventStatus.service);
          }
        });
      }),
      untilDestroyed(this)
    )
      .subscribe();
  }

  handleStenoInput(e: any) {
    if (e.data === ' ') {
      this.socket.emit('steno', { ID: this.eventId, text: this.stenoControl.value });
      this.stenoControl.patchValue('');
    }
  }

  downloadSubtitles() {
    this.appModalService
      .show<ExportSubtitlesComponent>(ExportSubtitlesComponent, undefined, '400px')
      .afterClosed()
      .subscribe(res => {
        if (res === true)
          return;

        this.eventService.downloadSubtitles(this.eventId, res.type, res.language, res.timing, res.lineLength);
      });
  }

  beginBypass() {
    this.speech.bypassRecording();
    this.eventService.disableEncoders(this.eventId).subscribe(() => {
      this.appModalService
      .show<EnableBypassComponent>(EnableBypassComponent, undefined, '400px')
      .afterClosed()
      .subscribe(() => {
        this.endBypass();
      });
    });    
  }

  endBypass() {
    this.eventService.enableEncoders(this.eventId).subscribe();
    this.speech.unbypassRecording();
  }
}
